<script lang="ts" module>
  import { Select } from 'bits-ui';
  import { tv, type VariantProps } from 'tailwind-variants';

  import Icon from '@/ui/base/icon/Icon.svelte';

  import DropDownSelect, { type DropDownItem } from './DropDown/DropDownSelect.svelte';

  const styles = tv({
    base: ``,
    variants: {
      open: {
        true: 'bg-dropdown-S',
        false: 'bg-dropdown hocus:bg-dropdown-H',
      },
      disabled: {
        true: '!bg-dropdown-D',
        false: '',
      },
      variant: {
        default:
          'flex items-center justify-between gap-1 rounded-sm py-2 pl-2 pr-1 font-sans text-sm font-medium text-c-base',
        button:
          'border-gradient fr relative grow-0 gap-2 rounded-md border from-border-gradient-start to-border-gradient-end px-4 py-1 text-lsm hocus:to-border-gradient-end-H',
      },
    },
    defaultVariants: {
      variant: 'default',
      open: false,
      disabled: false,
    },
  });

  export interface DropDownProps {
    items: DropDownItem[];
    selected?: string;
    onItemClick?: (item: string) => void;
    disabled?: boolean;
    ariaLabel?: string;
    placeholder?: string;
    class?: string;
    withCheckbox?: boolean;
    variant?: VariantProps<typeof styles>['variant'];
  }
</script>

<script lang="ts">
  let {
    selected,
    withCheckbox = false,
    variant,
    class: className,
    ...props
  }: DropDownProps = $props();

  let selectedLabel: string | undefined = $derived(
    selected ? props.items.find((item) => item.value === selected)?.label : undefined,
  );
  let isOpen: boolean = $state(false);
</script>

<Select.Root
  disabled={props.disabled}
  bind:open={isOpen}
  type="single"
  onValueChange={(v) => {
    props.onItemClick?.(v);
  }}
  value={selected}
>
  <Select.Trigger
    class={`${styles({ open: isOpen, disabled: props.disabled, variant, class: className })} ${!selectedLabel && props.placeholder ? 'text-c-light' : ''}`}
    aria-label={props.ariaLabel ?? 'Select'}
  >
    {selectedLabel ?? props.placeholder ?? '...'}
    {#if withCheckbox && selectedLabel}
      <Icon icon="CheckCircleOutline" size={4} class="ml-1 text-c-success" />
    {/if}
    <Icon icon="KeyboardArrowDown" class="size-4" />
  </Select.Trigger>

  <Select.Portal>
    <Select.Content align="end" sideOffset={4} class="z-50">
      <Select.Viewport class="grid gap-1 rounded-sm bg-dropdown-S p-1">
        {#each props.items as item (item)}
          <Select.Item value={item.value} label={item.label} disabled={item.disabled}>
            <DropDownSelect selected={item.value === selected} {item}></DropDownSelect>
          </Select.Item>
        {/each}
      </Select.Viewport>
    </Select.Content>
  </Select.Portal>
</Select.Root>
